/**
 * The conversation between the couple's invitation editor (app/) and the
 * preview of their invitation drawn here, in an iframe on the editor page.
 *
 * The editor owns the data: it reads the invitation with the couple's own
 * session and sends it in, unsaved edits included — this page never fetches
 * anything and never sees a token. Every message carries CHANNEL and VERSION,
 * and is only accepted from the editor's origin (VITE_APP_URL) and from the
 * window that framed us.
 *
 * Mirrored in app/src/services/invitation/previewProtocol.ts — keep the two in
 * step.
 *
 * Editor → preview
 *   render         { payload, lang: "en"|"el", editable: boolean }
 *   focus-section  { section }          scroll to a section and mark it
 * Preview → editor
 *   ready          {}                   mounted; send the first render
 *   rendered       { sections }         the sections actually drawn, in order
 *   defaults       { lang, eventType, texts }  the template's own lines
 *   select-section { section }          the couple clicked a section
 *   text-input     { slot, value }      the couple typed in a line on the preview
 */

export const CHANNEL = "agapae:invitation-preview";
export const VERSION = 1;

export const EDITOR_MESSAGES = Object.freeze({
  RENDER: "render",
  FOCUS_SECTION: "focus-section",
});

export const PREVIEW_MESSAGES = Object.freeze({
  READY: "ready",
  RENDERED: "rendered",
  DEFAULTS: "defaults",
  SELECT_SECTION: "select-section",
  TEXT_INPUT: "text-input",
});

/** The editor's origin, from VITE_APP_URL; null when it is not configured. */
export function editorOrigin() {
  try {
    return new URL(import.meta.env.VITE_APP_URL).origin;
  } catch {
    return null;
  }
}

/** A message from the editor this page should act on, or null. */
export function readEditorMessage(event) {
  const origin = editorOrigin();
  if (!origin || event.origin !== origin) return null;
  if (event.source !== window.parent) return null;
  const data = event.data;
  if (!data || data.channel !== CHANNEL || data.version !== VERSION) return null;
  if (!Object.values(EDITOR_MESSAGES).includes(data.type)) return null;
  return data;
}

/** Tell the editor something; a no-op when not framed or not configured. */
export function postToEditor(type, data = {}) {
  const origin = editorOrigin();
  if (!origin || window.parent === window) return;
  window.parent.postMessage({ channel: CHANNEL, version: VERSION, type, ...data }, origin);
}
